import { graphql } from 'gatsby';

import { styled } from '@mui/material';

import { Tabs } from '@mewa/ui';

import { isAdvantages, StrapiAdvantages } from './advantages';
import { isAwards, StrapiAwards } from './awards';
import { isCosts, StrapiCosts } from './costs';
import { isMewaCircle, StrapiMewaCircle } from './mewa-circle';
import { isMewaSteps, StrapiMewaSteps } from './mewa-steps';
import { MewaEssentialsProps, MewaEssentialsSection } from './types';

export const MewaEssentialsFragment = graphql`
  fragment MewaEssentialsFragment on STRAPI_MEWA_ESSENTIAL {
    sections {
      ...AdvantagesFragment
      ...AwardsFragment
      ...MewaStepsFragment
      ...MewaCircleEssentialsFragment
      ...CostsFragment
    }
  }
`;

export const StrapiMewaEssentials = ({
  mewaEssentials,
  lang,
  pressOverviewSlug,
  hideCostSection = false,
  ...dataProps
}: MewaEssentialsProps) => {
  const selectSectionType = (section: MewaEssentialsSection) => {
    if (isAdvantages(section)) {
      return <StrapiAdvantages data={section} />;
    } else if (isMewaCircle(section)) {
      return <StrapiMewaCircle data={section} />;
    } else if (isMewaSteps(section)) {
      return <StrapiMewaSteps data={section} />;
    } else if (isCosts(section)) {
      return <StrapiCosts data={section} />;
    } else if (isAwards(section)) {
      return (
        <StrapiAwards
          data={section}
          lang={lang}
          pressOverviewSlug={pressOverviewSlug}
        />
      );
    }

    return null;
  };

  // Do not render the cost section if there are no cost items for clothing/nonClothing
  const sections =
    mewaEssentials?.sections?.filter(
      (section) =>
        !isCosts(section) || (!hideCostSection && section.costItems.length > 0),
    ) ?? [];

  // only render tabs if number of sections is not 0.
  return sections.length > 0 ? (
    <Tabs
      sections={sections.map((section) => ({
        name: section.tabTitle,
        title: section.tabTitle,
        content: selectSectionType(section),
      }))}
      {...dataProps}
    />
  ) : null;
};

export const Description = styled('p')(({ theme }) => ({
  ...theme.typography.h5,
  width: '100%',
  margin: '0 0 25px',

  [theme.breakpoints.up('sm')]: {
    textAlign: 'center',
    marginBottom: '40px',
  },

  [theme.breakpoints.up('md')]: {
    marginBottom: '50px',
  },

  [theme.breakpoints.up('lg')]: {
    marginBottom: '60px',
  },
}));

export const TextContainer = styled('div')(({ theme }) => ({
  margin: '0 auto',

  [theme.breakpoints.up('md')]: {
    width: 'calc(100% - 33.33% + 2 * var(--gutter))',
  },
}));
